import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, developedTestimonials, faqs, modules, pains, testimonials } from '../data/content';
import { Button } from './ui/Button';
const collapseTransition = {
  duration: 0.22,
  ease: [0.23, 1, 0.32, 1] as const
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-extrabold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-primary'}`} data-id="element-16" data-mp="1ip9rjp7f7dbx">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-black leading-tight text-ink md:text-4xl" data-id="element-17" data-mp="2z2817ogoi9wv">
      {children}
    </h2>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-12 md:py-14" data-id="element-18" data-mp="1bq6u8sh5a4zg">
      <Eyebrow data-id="element-19" data-mp="1bnay99ouxqdn">⭐ 4,9/5 · +200 personas ya lo usan</Eyebrow>
      <SectionTitle data-id="element-20" data-mp="16rvlym6i02jc">
        Lo que dicen los que <span className="text-primary" data-id="element-21" data-mp="16or8ey757ug0">ya recuperaron su espalda</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-22" data-mp="12qtmq5ckzqhd">
        {testimonials.map(item => <article key={item.name} className="relative flex flex-col rounded-2xl border border-sky-100 bg-white p-6 shadow-sm" data-id="element-23" data-mp="xs0aeg9165is">
            <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-primary/15" data-id="element-24" data-mp="nzay5j5nonme" />
            <div className="mb-3 text-sm text-gold" data-id="element-25" data-mp="j080ijs09jog">⭐⭐⭐⭐⭐</div>
            <p className="mb-5 flex-1 text-base font-semibold italic leading-relaxed text-slate-700" data-id="element-26" data-mp="ezegi1hoop0u">
              “{item.text}”
            </p>
            <div className="mt-auto flex items-center gap-3 border-t border-sky-100 pt-4" data-id="element-27" data-mp="9za00bgrzuam">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-28" data-mp="529q1pgt27e8">
                {item.initials}
              </span>
              <div data-id="element-29" data-mp="eadhutb44uy">
                <p className="text-sm font-extrabold text-ink" data-id="element-30" data-mp="amj6tg1fef6">{item.name}</p>
                <p className="text-xs text-slate-500" data-id="element-31" data-mp="3hmdugz8qe7g2">📍 {item.place}</p>
              </div>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Pain() {
  return <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-14" data-id="element-32" data-mp="e4vybp2bmovh">
      <div className="mx-auto max-w-4xl" data-id="element-33" data-mp="j1vsp2pt636r">
        <Eyebrow danger data-id="element-34" data-mp="j51h0fqm511f">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-35" data-mp="4d9g38rkfpj8">
          ¿Cuánto más vas a vivir con <span className="text-primary" data-id="element-36" data-mp="4cgdmjzn3m6w">miedo a tu propia espalda?</span>
        </SectionTitle>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-37" data-mp="9b16tzgh55lu">
          {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-sky-100 bg-canvas p-4" data-id="element-38" data-mp="x0vk2i4pgw6m">
              <XIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-39" data-mp="2mj24w3bfe1kp" />
              <span className="text-sm font-semibold text-slate-700 md:text-base" data-id="element-40" data-mp="2hlabf4v4u7ee">{pain}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-41" data-mp="2w8loxcavk1h5">
          No es mala suerte.{' '}
          <span className="font-black text-primary" data-id="element-42" data-mp="2rblrtqw06926">Es falta de un sistema claro.</span>
        </p>
        <div className="mt-7 text-center" data-id="element-43" data-mp="23lhpdmujwfcr">
          <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-44" data-mp="1ymobhtbcqmc2">
            SÍ, QUIERO RECUPERAR MI ESPALDA →
          </Button>
        </div>
      </div>
    </section>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-14" data-id="element-45" data-mp="3o12vk0bfdl18">
      <Eyebrow data-id="element-46" data-mp="3nzi1ebt0w43w">
        <span className="inline-flex items-center gap-2" data-id="element-47" data-mp="3o35u58qn8mhk">
          <TrophyIcon className="h-4 w-4" data-id="element-48" data-mp="3jyg1dsra7o0v" /> Qué incluye el sistema
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-49" data-mp="j5bk6cxdukve">
        4 módulos. <span className="text-primary" data-id="element-50" data-mp="j632wn5vso49">Recuperación completa → Protección total.</span>
      </SectionTitle>
      <p className="mt-3 text-center font-semibold text-slate-600" data-id="element-51" data-mp="n7y4ztvmz5j3">
        215+ páginas con ejercicios, técnicas y rutinas paso a paso.
      </p>

      <div className="mt-8 space-y-3" data-id="element-52" data-mp="x4b7nh95stpz">
        {modules.map((mod, index) => <div key={mod.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary shadow-lg shadow-sky-100' : 'border-sky-100'}`} data-id="element-53" data-mp="123e0827yd7h2" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-54" data-mp="1avzjrkjeri6y">
              <span className="flex items-center gap-3" data-id="element-55" data-mp="1psgty8fv2qrx">
                <span className="text-2xl" data-id="element-56" data-mp="1psymatzekkuk">{mod.icon}</span>
                <span data-id="element-57" data-mp="3ab13vqu86s0d">
                  <b className="block text-sm font-black uppercase text-ink md:text-base" data-id="element-58" data-mp="3abivga5y0i1y">
                    {mod.name}
                  </b>
                  <small className="font-bold text-primary" data-id="element-59" data-mp="30clatibn18o8">{mod.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-60" data-mp="2ve089c8wycwm" />
            </button>
            <AnimatePresence initial={false} data-id="element-61" data-mp="3o1w2mab0fsq8">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} data-id="element-62" data-mp="3jb5w2tquljty">
                  <ul className="space-y-2 border-t border-sky-100 px-5 py-4 text-sm font-semibold text-slate-600 md:px-6" data-id="element-63" data-mp="fofel2k2ca0h">
                    {mod.content.map(line => <li key={line} className="flex gap-2" data-id="element-64" data-mp="254srz68cir5i">
                        <span className="text-primary" data-id="element-65" data-mp="2a32p3mdms1tr">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>

      <div className="mt-8 rounded-2xl border-2 border-primary/20 bg-canvas p-6 text-center" data-id="element-66" data-mp="20aev0ze1cbyq">
        <p className="text-base font-black text-ink md:text-xl" data-id="element-67" data-mp="1mfcrgiwnuyp3">
          Empezá hoy por el Módulo 1 y sentí el primer alivio esta misma semana.
        </p>
        <div className="mt-5" data-id="element-68" data-mp="1cfu6q08xf8vz">
          <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-69" data-mp="1cgwzyetq1nf2">
            💪 DESBLOQUEAR LOS 4 MÓDULOS POR $19.990 →
          </Button>
        </div>
        <p className="mt-3 text-[11px] font-semibold text-slate-500" data-id="element-70" data-mp="36u4wqc19c93c">
          Acceso inmediato · Garantía de 7 días
        </p>
      </div>
    </section>;
}
export function BonusesSection() {
  return <section className="border-y border-sky-100 bg-white px-4 py-12 md:py-14" data-id="element-71" data-mp="1052x5l681dn0">
      <div className="mx-auto max-w-6xl" data-id="element-72" data-mp="v6j99f82hrs0">
        <Eyebrow data-id="element-73" data-mp="v3do2b2k7a1k">
          <span className="inline-flex items-center gap-2" data-id="element-74" data-mp="19ue7fb4ugoch">
            <GiftIcon className="h-4 w-4" data-id="element-75" data-mp="19vp7p0z2zdyu" /> 3 bonos siempre incluidos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-76" data-mp="152cxvwz29bq5">
          Además te llevás <span className="text-primary" data-id="element-77" data-mp="1iussc9dwc1fg">3 bonos gratis</span>
        </SectionTitle>
        <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-78" data-mp="1dvzpdaw8jstj">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-sky-100 bg-canvas p-5 shadow-sm" data-id="element-79" data-mp="1stib04dm9pwr">
              <div className="flex gap-3" data-id="element-80" data-mp="1nsb4e8n7mwro">
                <span className="text-3xl" data-id="element-81" data-mp="3ulmz14rzw327">{icon}</span>
                <div data-id="element-82" data-mp="3umg7qbail2mz">
                  <small className="font-extrabold uppercase tracking-wide text-primary" data-id="element-83" data-mp="3pm202uctruph">
                    Bono {number}
                  </small>
                  <h3 className="text-base font-black uppercase leading-tight text-ink" data-id="element-84" data-mp="33s32b00taeyx">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-85" data-mp="3dieipm1omgmp">
                {description}
              </p>
              <div className="mt-auto flex items-center border-t border-sky-100 pt-4 text-sm font-extrabold" data-id="element-86" data-mp="3ifxxcn2akvf6">
                <span className="text-slate-400 line-through" data-id="element-87" data-mp="2kv1xpgb66gvi">{value}</span>
                <span className="ml-auto rounded-full bg-primary/10 px-3 py-1 text-primary" data-id="element-88" data-mp="2ptmu14nawgeo">
                  GRATIS
                </span>
              </div>
            </article>)}
        </div>
        <p className="mt-7 text-center text-base font-bold text-slate-600 md:text-lg" data-id="element-89" data-mp="2yu1k0hb7mjoi">
          Valor total de bonos: <s className="text-slate-400" data-id="element-90" data-mp="a0wahdhzz7lr">$34.000</s> →{' '}
          <span className="font-black text-primary" data-id="element-91" data-mp="e2hurjwv2o5z">incluidos gratis hoy</span>
        </p>
      </div>
    </section>;
}
export function ResultsAndGuarantee() {
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-14" data-id="element-92" data-mp="1ymwebkh1qv37">
      <Eyebrow data-id="element-93" data-mp="1uk04ai5ko7av">❤️ Resultados reales</Eyebrow>
      <SectionTitle data-id="element-94" data-mp="1psrsxgkombv2">
        Personas que ya <span className="text-primary" data-id="element-95" data-mp="1prq84kdv6b05">recuperaron su espalda</span>
      </SectionTitle>

      <div className="mt-8 space-y-4" data-id="element-96" data-mp="1kugjh6yau3dd">
        {developedTestimonials.map(item => <article key={item.name} className="rounded-2xl border border-sky-100 bg-white p-5 shadow-sm md:flex md:gap-6 md:p-6" data-id="element-97" data-mp="1fv3ofyx9xpzp">
            <div className="flex items-center gap-3 md:w-60 md:shrink-0 md:flex-col md:items-start" data-id="element-98" data-mp="1axu1cpxcxqf1">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-99" data-mp="16vqlzq7ki1oh">
                {item.initials}
              </span>
              <div data-id="element-100" data-mp="2w93ln24qj1j9">
                <p className="text-sm font-extrabold text-ink" data-id="element-101" data-mp="2w8k9k21rn7d5">{item.name}</p>
                <p className="text-xs font-semibold leading-snug text-slate-500" data-id="element-102" data-mp="254wd5vp4awr3">📍 {item.place}</p>
              </div>
            </div>
            <div className="mt-4 border-t border-sky-100 pt-4 md:mt-0 md:flex-1 md:border-l md:border-t-0 md:pl-6 md:pt-0" data-id="element-103" data-mp="205a0bnd7biit">
              <div className="flex flex-wrap items-center gap-3" data-id="element-104" data-mp="2e3qgs60v8ne5">
                <span className="text-sm text-gold" data-id="element-105" data-mp="297swxpqe8ywm">⭐⭐⭐⭐⭐</span>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-primary" data-id="element-106" data-mp="1lgvy9enlzy3b">
                  {item.result}
                </span>
              </div>
              <p className="mt-3 text-sm font-semibold italic leading-relaxed text-slate-700" data-id="element-107" data-mp="1v7i9kwoam2g7">
                “{item.text}”
              </p>
            </div>
          </article>)}
      </div>

      <div className="mt-10 flex flex-col items-center gap-5 rounded-3xl border-2 border-primary/20 bg-white p-6 text-center shadow-sm md:flex-row md:p-8 md:text-left" data-id="element-108" data-mp="12q5sni6nlx6r">
        <span className="inline-flex shrink-0 rounded-full border-2 border-primary bg-canvas p-4" data-id="element-109" data-mp="xpqguv20sxqw">
          <ShieldIcon className="h-9 w-9 text-primary" data-id="element-110" data-mp="39d3kfg0o79hj" />
        </span>
        <div data-id="element-111" data-mp="3e6xxw1poms86">
          <h3 className="text-lg font-black uppercase text-ink md:text-xl" data-id="element-112" data-mp="3e7r9bfzl65wn">Garantía de 7 días</h3>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-113" data-mp="30c5cwnr0wju7">
            Abrís los módulos y los usás. Si no es lo que esperabas, escribís a
            compradigitalyaa@gmail.com y te devolvemos el 100%. Sin formularios, sin drama, sin
            “pero…”. Tasa de devoluciones: <b className="text-primary" data-id="element-114" data-mp="ybllohv1ir6">menos del 0,3%</b>.
          </p>
        </div>
      </div>
    </section>;
}
export function FAQSection() {
  const [open, setOpen] = useState(0);
  return <section className="border-t border-sky-100 bg-white px-4 py-12 md:py-14" data-id="element-115" data-mp="3o1zoz33iq4de">
      <div className="mx-auto max-w-3xl" data-id="element-116" data-mp="27ot21au860gq">
        <Eyebrow data-id="element-117" data-mp="2conql7qj2dym">
          <span className="inline-flex items-center gap-2" data-id="element-118" data-mp="2cp6x7saq9fxc">
            <HelpCircleIcon className="h-4 w-4" data-id="element-119" data-mp="2cmkpnsnu70f4" /> Preguntas frecuentes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-120" data-mp="1sonlgaszowf1">
          Lo que más <span className="text-primary" data-id="element-121" data-mp="1okfsg1fi0ub0">nos preguntan</span>
        </SectionTitle>
        <div className="mt-8 space-y-3" data-id="element-122" data-mp="1ju929x5c91qn">
          {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary/50 shadow-lg shadow-sky-100' : 'border-sky-100'}`} data-id="element-123" data-mp="1ev5y0ex6mzjq" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-3 p-5 text-left text-sm font-extrabold text-ink md:text-base" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-124" data-mp="14vf4ogkcyql8">
                {question}
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-125" data-mp="2v7blkzyto5ei" />
              </button>
              <AnimatePresence initial={false} data-id="element-126" data-mp="2xrzr8jsvb2ad">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={collapseTransition} className="border-t border-sky-100 px-5 py-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-127" data-mp="32qu9nkmxaoty">
                    {answer}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
        <div className="mt-9 rounded-2xl bg-canvas p-6 text-center" data-id="element-128" data-mp="2sv95pgyanehk">
          <p className="text-base font-black text-ink md:text-xl" data-id="element-129" data-mp="42x59krij404">
            El próximo día puede ser diferente.
          </p>
          <p className="mt-1 text-sm font-semibold italic text-primary" data-id="element-130" data-mp="83qt6e4ygit0">
            “Finalmente una mañana sin pensar en mi espalda” 💪
          </p>
          <div className="mt-5" data-id="element-131" data-mp="6pfofxui2qq1">
            <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-132" data-mp="1oi7fol882q4">
              QUIERO MI COMBO LUMBAR AHORA →
            </Button>
          </div>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500" data-id="element-133" data-mp="gjg1clw0x09b">
            <span className="flex items-center gap-1" data-id="element-134" data-mp="plxl8to7hgt5">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-135" data-mp="pke99f9l1rx6" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-136" data-mp="kmuijfvp7moc">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-137" data-mp="zca50jo755y8" /> Acceso en 5 minutos
            </span>
            <span className="flex items-center gap-1" data-id="element-138" data-mp="ucntqfrxhzs2">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-139" data-mp="ufa04c33298p" /> Pago seguro
            </span>
          </p>
        </div>
      </div>
    </section>;
}